import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SKELETON — A LOADING PLACEHOLDER, AND THE REGION THAT OWNS ITS STATE.
 * ═══════════════════════════════════════════════════════════════════════════
 * Follows the five rules in `Button.tsx`. Two exports, and they are two halves of one pattern:
 * `Skeleton` is what a sighted user sees while data is in flight, `SkeletonRegion` is what
 * everyone else gets instead. Shipping only the first is the bug this file exists to prevent.
 *
 * ── 1. THE SKELETON ITSELF IS `aria-hidden`, UNCONDITIONALLY ───────────────
 * It is a grey rectangle standing in for text that does not exist yet. Left in the
 * accessibility tree it is either silence (a `<div>` with no content has no accessible name) or,
 * worse, a screen reader dutifully reporting a dozen empty groups where the extension list is
 * about to be — a page that reads as broken rather than as busy. There is no prop to turn this
 * off, because there is no case where announcing a placeholder is better than announcing that
 * the region is loading.
 *
 * That hiding is only safe because a skeleton contains nothing focusable: `aria-hidden` over a
 * focusable node produces an element the keyboard can reach and the screen reader cannot name,
 * which is a worse failure than the one being fixed. Never put a control inside one.
 *
 * ── 2. SO THE STATE HAS TO BE SOMEWHERE ELSE: `aria-busy` ON THE CONTAINER ─
 * `SkeletonRegion` is that container. `aria-busy="true"` on the element whose content is
 * pending is the ARIA-native way to say "this part is mid-update"; it is a global attribute, it
 * needs no role, and it removes the whole subtree from "this is the final content" without
 * removing it from the page.
 *
 * ── 3. WHY THERE IS NO "Loading…" ANNOUNCEMENT, AND THERE IS A "loaded" ONE ─
 * The obvious addition is a polite live region reading "Loading extensions" while busy. It is
 * left out on purpose: that region would sit INSIDE the `aria-busy="true"` subtree, and
 * `aria-busy` is a licence for assistive technology to defer presenting changes in that subtree
 * until it clears — so the announcement is the one thing in the component that is allowed to
 * arrive late or not at all, and testing it would show it working in whichever screen reader
 * happened to be open. `aria-busy` already carries "busy" to anyone who lands there.
 *
 * The COMPLETION is different and is worth announcing, because a keyboard user who tabbed away
 * to wait has no other signal: by the time the text changes, `aria-busy` is gone and the region
 * is an ordinary live region again. So `readyLabel` is announced on the busy → ready
 * transition, and only then — a region that mounts already ready contains its text from the
 * first paint, and a live region that arrives with its content complete has no mutation to
 * observe and is (correctly, here) silent. `Field.tsx` documents the same mechanism from the
 * other side, where it is a trap rather than a feature.
 *
 * ── 4. REDUCED MOTION IS ALREADY HANDLED — DO NOT ADD IT HERE ─────────────
 * `styles.css` collapses every animation to 0.01ms under `prefers-reduced-motion: reduce`
 * globally, which stops the pulse dead for the users who asked for that (WCAG 2.3.3). A
 * `motion-reduce:animate-none` here would be a second, differently-implemented answer to the
 * same question — and the global rule deliberately does NOT use `animation: none` because an
 * element mid-cycle when the preference flips would freeze part-way. Duplicating it with
 * `none` would reintroduce exactly the artefact that rule avoids.
 */

export type SkeletonShape = 'text' | 'block' | 'circle';

/**
 * Sizes are defaults to be overridden with `className`, not a scale. A skeleton is only
 * convincing when it is the size of the thing it replaces, and only the caller knows that —
 * `text` matches a `text-sm` line, `circle` an avatar, `block` a card.
 */
const SHAPES: Record<SkeletonShape, string> = {
  text: 'h-4 w-full rounded-sm',
  block: 'h-20 w-full rounded-md',
  circle: 'size-10 rounded-full',
};

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  shape?: SkeletonShape;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { shape = 'text', className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('animate-pulse bg-surface-sunken', SHAPES[shape], className)}
      {...rest}
      // AFTER the spread, deliberately — this is the one attribute a caller must not be able to
      // clear by passing `aria-hidden={false}` through the native props. See header §1.
      aria-hidden="true"
    />
  );
});

export interface SkeletonRegionProps extends HTMLAttributes<HTMLDivElement> {
  /** While true the region is `aria-busy` and its content is understood to be provisional. */
  busy: boolean;
  /**
   * Announced once, when `busy` goes false — "Extensions loaded". Omit it and the region makes
   * no announcement at all, which is the right choice for something the user is watching. See
   * header §3 for why there is no `busyLabel` counterpart.
   */
  readyLabel?: string;
}

export const SkeletonRegion = forwardRef<HTMLDivElement, SkeletonRegionProps>(
  function SkeletonRegion({ busy, readyLabel, className, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        // `|| undefined` rather than `aria-busy={busy}`: `aria-busy="false"` is the default
        // state spelled out, and an attribute that is present-and-false is one more thing for a
        // test — or a reader of the DOM — to have to interpret.
        aria-busy={busy || undefined}
        className={cn(className)}
        {...rest}
      >
        {/**
         * Mounted for the whole life of the region and rendered EMPTY while busy, so the
         * transition is a mutation of a region assistive technology is already watching. Only
         * rendered at all when the caller opted in, because an always-present `<span
         * role="status">` that never says anything is a live region other tooling has to
         * account for and this one would never fire.
         */}
        {readyLabel !== undefined ? (
          <span role="status" className="sr-only">
            {busy ? '' : readyLabel}
          </span>
        ) : null}
        {children}
      </div>
    );
  },
);
